//#region types
import {
  AgentAnswerReply,
  AgentCardRow,
  AgentCaseSummary,
  AgentConversationLine,
  AgentTurn,
  outcomeOf,
  SupportCaseContent,
} from "models/agent/agentContract";
import { TranslationKeys } from "translations";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

interface SupportCasePost {
  subject: string;
  message: string;
}

type Translate = (key: string) => string;

const SubjectMaxLength = 80;

// Cutting loses nothing: her full question is the message.
const toSubject = (text: string): string => {
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= SubjectMaxLength) return flat;

  const cut = flat.slice(0, SubjectMaxLength);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

// The answer as she read it, as plain text for the summary. Its URLs are broken: the model writing the case title
// reads these lines, the case view turns URLs into links, and the answer is written by a model that reads customer data.
const answerLine = (reply: AgentAnswerReply): string =>
  outcomeOf(reply) === "Blocked"
    ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SupportIssue.AgentAnswerBlocked)
    : reply.text.replace(/\*\*/g, "").replace(/(https?):\/\//gi, "$1[:]//");

// What the agent said, as one transcript line. A case draft's bubble is client copy, so it is left out.
const agentLine = (turn: AgentTurn): string | undefined => {
  const reply = turn.reply;
  if (!reply) return turn.text;

  switch (reply.kind) {
    case "answer":
      return answerLine(reply);
    case "question":
      return reply.text;
    case "card":
    case "receipt":
      return reply.title;
    default:
      return undefined;
  }
};

/** The question as conversation lines, for the model that writes the case title and category. */
export const toConversationLines = (turns: AgentTurn[]): AgentConversationLine[] =>
  turns.flatMap((turn) => {
    const text = (turn.who === "customer" ? turn.text : agentLine(turn))?.trim();
    return text ? [{ who: turn.who, text }] : [];
  });

export const firstQuestionText = (question: AgentTurn[]): string | undefined =>
  question.find((turn) => turn.who === "customer" && turn.text?.trim())?.text?.trim();

export const localCaseContent = (question: AgentTurn[]): SupportCaseContent => ({
  subject: toSubject(firstQuestionText(question) ?? ""),
  category: null,
  drafted: false,
  question: firstQuestionText(question) ?? "",
});

export const summarizedCaseContent = (question: AgentTurn[], summary: AgentCaseSummary): SupportCaseContent => ({
  subject: summary.subject,
  category: summary.category,
  drafted: summary.drafted,
  question: firstQuestionText(question) ?? "",
});

export const caseDetailRows = (content: SupportCaseContent, fileNames: string[], t: Translate): AgentCardRow[] =>
  [
    { label: t(TranslationKeys.SupportIssue.AgentRowConcerns), value: content.category ?? "" },
    { label: t(TranslationKeys.SupportIssue.AgentRowTitle), value: content.subject },
    { label: t(TranslationKeys.SupportIssue.AgentRowAttachments), value: fileNames.join(", ") },
  ].filter((row) => row.value.trim());

/**
 * The case message is her first question as she wrote it. Support reads the rest of the conversation in the agent's
 * log, which Core posts on a linked case; an unlinked one goes without it.
 */
export const buildSupportCasePost = (content: SupportCaseContent): SupportCasePost => ({
  subject: content.subject,
  message: content.question,
});
